import { Empty, IconFont, Input, pointer, Popover, Text, tinyButtons } from '@byecode/ui'
import { type VariableADTvalue, VariableType } from '@lighthouse/core'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'

import { getVariableValue, isVariableItemActive } from '../../help'
import type { VariableTree2 } from '../../types'
import { ListItem } from '../ListItem'

const SCxSearchWrapper = styled.div`
    padding: 4px 12px 0;
    margin-bottom: 8px;
`

const SCxListContainer = styled.div`
    width: 100%;
    height: auto;
    max-height: 600px;
    overflow: hidden auto;
    ${tinyButtons}
`

const SCxExtra = styled.div`
    padding: 0 16px;
    height: 36px;
    display: flex;
    align-items: center;
    color: var(--color-gray-500);
    font-size: var(--font-size-sm);
`

interface VariableItemProps {
    data: VariableTree2
    value?: VariableADTvalue
    onChange: (value: VariableADTvalue) => void
    path: string[]
    parentSelected?: boolean
}

export const VariableItem: React.FunctionComponent<VariableItemProps> = ({ parentSelected, data, value, onChange, path }) => {
    const selected = useMemo(
        () => (parentSelected === undefined ? true : parentSelected) && isVariableItemActive(data, value),
        [data, parentSelected, value]
    )
    const [searchLabel, setSearchLabel] = useState('')

    const handleChange = useCallback(() => {
        onChange?.(selected ? { type: VariableType.VALUE } : getVariableValue(data, path))
    }, [data, onChange, path, selected])

    const filterData = useMemo(() => {
        const searchVal = searchLabel.trim()
        if (!searchVal) {
            return data.children || []
        }

        return data.children ? data.children.filter(item => item.label.includes(searchVal)) : []
    }, [data.children, searchLabel])

    const dom = (
        <ListItem
            data={data}
            selected={selected}
            showArrowIcon={!!data.children}
            onChange={() => {
                if (data.children) {
                    return
                }
                handleChange()
            }}
        />
    )

    if (data.children) {
        return (
            <Popover position="left-start">
                <Popover.Target>{dom}</Popover.Target>
                <Popover.Dropdown>
                    {data.searchable && (
                        <SCxSearchWrapper>
                            <Input
                                value={searchLabel}
                                onChange={e => setSearchLabel(e.target.value)}
                                prefix={<IconFont type="Search" />}
                                clearable
                                onClear={() => setSearchLabel('')}
                                placeholder="搜索"
                            />
                        </SCxSearchWrapper>
                    )}

                    {data.children.length === 0 ? (
                        <Empty
                            styles={{
                                content: {
                                    width: 250,
                                    height: 80,
                                    lineHeight: '80px',
                                    padding: '0 16px',
                                    textAlign: 'center'
                                }
                            }}
                            description="没有数据"
                        />
                    ) : (
                        <SCxListContainer>
                            {filterData.map(item => {
                                return (
                                    <VariableItem
                                        key={item.value}
                                        data={item}
                                        value={value}
                                        onChange={onChange}
                                        path={[...path, item.value]}
                                        parentSelected={selected}
                                    />
                                )
                            })}
                            <SCxExtra>
                                <Text size={12} color="var(--color-gray-500)">
                                    已隐藏不支持的类型
                                </Text>
                            </SCxExtra>
                        </SCxListContainer>
                    )}
                </Popover.Dropdown>
            </Popover>
        )
    }

    return dom
}
